import type { TFunction } from 'i18next';
import { isRecord } from '../finance-view-guards';
import type { ProducedRegister } from './prima-nota-model';

/** A source could not be asked again before generating, so nothing is generated. */
export class RefreshFailed extends Error {}

/** The server answered 4xx: it refused the action and changed nothing. */
export class Refused extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export type PageMessage = { kind: 'ok' | 'error'; text: string };

/** What every action needs of the page: the month, the words, and the page's own slots. */
export type ActionContext = {
  period: string;
  t: TFunction<'finance'>;
  setBusy: (busy: string | null) => void;
  setMessage: (message: PageMessage | null) => void;
  setProduced: (produced: ProducedRegister | null) => void;
  reload: () => void;
};

/**
 * The server's own words, when it refused for a reason the operator can act on.
 *
 * WHY THIS EXISTS. Every refusal on this screen was rendered as its HTTP status: the code threw
 * `new Error(String(result.response.status))` and the operator read "Generazione non riuscita:
 * 400". The server had said which sheet, which columns were missing and what to do about it —
 * `openapi-fetch` parses that body into `result.error` — and the screen discarded it one layer
 * above the seam. A test in this very file already said so in its own words: "into a 400 the
 * operator cannot read."
 *
 * That made every carefully written Italian refusal in the platform unreachable, which is worse
 * than not writing them: the effort reads as done. A missing-column refusal is the case that
 * forced it, because its whole value is the LIST it carries.
 *
 * Only `4xx` bodies are shown. A 5xx is a fault rather than a refusal, and its message is written
 * for whoever reads the logs, not for somebody trying to file a VAT register.
 */
export function refusalText(error: unknown, status: number): string | null {
  if (status < 400 || status >= 500 || !isRecord(error)) {
    return null;
  }
  const detail = error.detail ?? error.message;
  return typeof detail === 'string' && detail.trim() !== '' ? detail : null;
}

export function failureOf(error: unknown, status: number): Error {
  const text = refusalText(error, status) ?? String(status);
  return status >= 400 && status < 500 ? new Refused(text, status) : new Error(text);
}

/** Throws the failure of a response that did not come back with a yes. */
export function assertAnswered(result: {
  error?: unknown;
  response: { ok: boolean; status: number };
}): void {
  if (result.error !== undefined || !result.response.ok) {
    throw failureOf(result.error, result.response.status);
  }
}

/**
 * How every action on the month's files ends when it does not come back with a yes.
 *
 * A 4xx is a refusal: nothing changed, and the action's own words say why. The server answers
 * one only BEFORE it changes anything — the re-read after a change sits outside its refusal
 * mapping. Anything else — a 5xx, an answer lost on the way — may come after the change was
 * stored, so the screen does not claim a failure it cannot know: it says the action may have
 * happened, and puts away a register produced from the files as they were.
 *
 * The register stays only for a 400 or a 422, the server judging THIS request. Any other
 * refusal — a 404 for a file no longer live, a conflict — says the screen is behind the month,
 * so the register built from what it showed is put away too. The month is re-read every time.
 */
export function afterFailure(
  ctx: ActionContext,
  error: unknown,
  refused: (reason: string) => string,
) {
  const reason = error instanceof Error ? error.message : String(error);
  if (error instanceof Refused) {
    ctx.setMessage({ kind: 'error', text: refused(reason) });
    if (error.status !== 400 && error.status !== 422) {
      ctx.setProduced(null);
    }
  } else {
    ctx.setMessage({ kind: 'error', text: ctx.t('primaNota.uncertain', { reason }) });
    ctx.setProduced(null);
  }
  ctx.reload();
}

/**
 * One action at a time: the slot holds the action in flight, and every control that starts one
 * is locked while it is taken. The body's failure ends in `afterFailure`, and the slot is freed
 * whatever happens.
 */
export async function runGuarded(
  ctx: ActionContext,
  busyKey: string,
  refused: (reason: string) => string,
  body: () => Promise<void>,
) {
  ctx.setBusy(busyKey);
  ctx.setMessage(null);
  try {
    await body();
  } catch (error) {
    afterFailure(ctx, error, refused);
  } finally {
    ctx.setBusy(null);
  }
}
